import { useState, FormEvent, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Eye, EyeOff, LogIn, AlertCircle, Mail, ArrowLeft, RefreshCw, ShieldCheck, Lock } from 'lucide-react'

export default function LoginPage() {
  // Step 1: Credentials
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Step 2: Verification code
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Common state
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const { login, verifyCode, resendCode, cancelVerification, pendingVerification } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Obtener la ruta de destino (si existe)
  const from = (location.state as { from?: string })?.from || '/'

  // Focus first code input when entering verification step
  useEffect(() => {
    if (pendingVerification && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [pendingVerification])

  // Cooldown timer for resend button
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleCredentialsSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const result = await login(username, password)
      if (!result.requiresVerification) {
        // Direct login (shouldn't happen with 2FA)
        navigate(from, { replace: true })
      }
      // If requiresVerification is true, the pendingVerification state will be set
      // and the UI will switch to step 2
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newCode = [...verificationCode]
    newCode[index] = value
    setVerificationCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all digits are entered
    if (value && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        handleVerifyCode(fullCode)
      }
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newCode = [...verificationCode]
      for (let i = 0; i < 6; i++) {
        newCode[i] = pastedData[i] || ''
      }
      setVerificationCode(newCode)

      // Focus last filled input or submit
      const lastIndex = Math.min(pastedData.length - 1, 5)
      inputRefs.current[lastIndex]?.focus()

      if (pastedData.length === 6) {
        handleVerifyCode(pastedData)
      }
    }
  }

  const handleVerifyCode = async (code: string) => {
    setError('')
    setIsLoading(true)

    try {
      await verifyCode(code)
      navigate(from, { replace: true })
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Código inválido o expirado'
      setError(message)
      // Clear the code inputs
      setVerificationCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0) return

    setError('')
    setIsLoading(true)

    try {
      await resendCode()
      setResendCooldown(60) // 60 seconds cooldown
      setVerificationCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (err: any) {
      const message = err.response?.data?.detail || 'Error al reenviar el código'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    cancelVerification()
    setVerificationCode(['', '', '', '', '', ''])
    setError('')
  }

  // Step 2: Verification Code
  if (pendingVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900 px-4">
        <div className="max-w-md w-full">
          {/* Logo / Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary-600 dark:text-primary-400">
              JVS Informática
            </h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">
              Panel de Administración
            </p>
          </div>

          {/* Verification Card */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <ShieldCheck className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                Verificación de Seguridad
              </h2>
              <p className="mt-2 text-primary-100 text-sm">
                Hemos enviado un código de 6 dígitos a
              </p>
              <p className="mt-1 text-white font-medium flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                {pendingVerification.email_masked}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Error Message */}
              {error && (
                <div className="flex items-center gap-2 p-3 mb-6 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-800 rounded-lg text-danger-700 dark:text-danger-400">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Code Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 text-center">
                  Introduce el código de verificación
                </label>
                <div className="flex gap-2 justify-center" onPaste={handleCodePaste}>
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => inputRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      disabled={isLoading}
                      className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all disabled:opacity-50"
                    />
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={() => handleVerifyCode(verificationCode.join(''))}
                disabled={isLoading || verificationCode.some(d => !d)}
                className="btn btn-primary w-full flex items-center justify-center gap-2 py-3"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verificando...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Verificar y Acceder
                  </>
                )}
              </button>

              {/* Resend Code */}
              <div className="mt-4 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                  ¿No has recibido el código?
                </p>
                <button
                  onClick={handleResendCode}
                  disabled={isLoading || resendCooldown > 0}
                  className="inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código'}
                </button>
              </div>

              {/* Back Button */}
              <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={handleBack}
                  className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Volver al inicio de sesión
                </button>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-xs text-amber-700 dark:text-amber-300 text-center">
              El código expira en 10 minutos. Si no lo recibes, revisa tu carpeta de spam.
            </p>
          </div>

          {/* Footer */}
          <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
            &copy; {new Date().getFullYear()} JVS Informática. Todos los derechos reservados.
          </p>
        </div>
      </div>
    )
  }

  // Step 1: Credentials
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900 px-4">
      <div className="max-w-md w-full">
        {/* Logo / Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 dark:text-primary-400">
            JVS Informática
          </h1>
          <p className="mt-2 text-slate-600 dark:text-slate-400">
            Panel de Administración
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6 flex items-center gap-2">
            <LogIn className="w-5 h-5 text-primary-500" />
            Iniciar Sesión
          </h2>

          <form onSubmit={handleCredentialsSubmit} className="space-y-4">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-danger-50 dark:bg-danger-900/20 border border-red-200 dark:border-red-800 rounded-lg text-danger-700 dark:text-danger-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Usuario o Email
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="usuario@ejemplo.com"
                required
                autoComplete="username"
                autoFocus
              />
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !username || !password}
              className="btn btn-primary w-full flex items-center justify-center gap-2 py-3"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Verificando credenciales...
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Continuar
                </>
              )}
            </button>
          </form>

          {/* Security Note */}
          <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex items-start gap-3 text-xs text-slate-500 dark:text-slate-400">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
              <p>
                Tu cuenta está protegida con verificación en dos pasos.
                Recibirás un código en tu email para completar el acceso.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
          &copy; {new Date().getFullYear()} JVS Informática. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
